import type { Metrics, Value } from './presentationModel';
import { validDate } from './presentationModel';

export const reportViews = [
  'Daily CM Tracking',
  'Global Monthly CM Tracking',
  'Regions Monthly CM Tracking',
  'Comparision Overview',
  'Comparision Chart',
  'MTD Expectation',
  'Source health',
  'Sheet 68 · legacy bank fees',
] as const;
export type ReportView = (typeof reportViews)[number];
export type ReportState = {
  view: ReportView;
  from: string;
  to: string;
  market: string;
  basis: 'Net CM' | 'Gross CM';
  comparison: 'Previous Year' | 'Previous Period' | 'Previous Month' | 'Previous Quarter';
  cadence: 'Daily' | 'Weekly' | 'Monthly' | 'Monthly - MTD' | 'Quarterly' | 'Yearly' | 'Yearly - YTD';
  selectionComparison: string;
  year: string;
  master: 'Custom Date Range' | 'Date Selection';
  selectedDate: string;
  theme: 'light' | 'dark';
  filters: string;
  sort: 'Workbook order' | 'GM size';
  legacyRegion: string;
};
export const reportDefaults: ReportState = {
  view: 'Daily CM Tracking',
  from: '2026-05-01',
  to: '2026-10-17',
  market: 'Global - All Markets',
  basis: 'Net CM',
  comparison: 'Previous Year',
  cadence: 'Monthly',
  selectionComparison: 'MoM Same Month LY',
  year: '2026',
  master: 'Custom Date Range',
  selectedDate: '2026-10-17',
  theme: 'light',
  filters: 'show',
  sort: 'Workbook order',
  legacyRegion: 'Global - All Markets',
};
export const focusMarkets = [
  'AU',
  'EU',
  'UK',
  'NZ',
  'US + CA',
  'SG',
  'IN',
  'Asia + ME',
  'HK',
  'ZA',
  'ROW',
  'Global - All Markets',
  'DE',
  'FR',
];
export const reportMarkets = [
  'Global - All Markets',
  'AU',
  'UK',
  'US + CA',
  'NZ',
  'SG',
  'Asia + ME',
  'DE',
  'EU',
  'FR',
  'IN',
  'ROW',
  'HK',
  'ZA',
];

export function parseReport(q: string, prefix = ''): ReportState {
  const p = new URLSearchParams(q);
  const state = {
    ...reportDefaults,
    ...Object.fromEntries(
      Object.keys(reportDefaults).map((k) => [k, p.get(prefix + k) ?? reportDefaults[k as keyof ReportState]]),
    ),
  };
  const old: Record<string, ReportView> = {
    Comparison: 'Comparision Overview',
    Daily: 'Daily CM Tracking',
    Monthly: 'Global Monthly CM Tracking',
  };
  state.view = old[state.view] ?? state.view;
  if (state.market === 'All markets') {
    state.market = 'Global - All Markets';
  }
  if (state.comparison === ('Previous year' as string)) {
    state.comparison = 'Previous Year';
  }
  if (state.comparison === ('Previous period' as string)) {
    state.comparison = 'Previous Period';
  }
  return state;
}
export function reportQuery(s: ReportState, prefix = '') {
  return new URLSearchParams(Object.fromEntries(Object.entries(s).map(([k, v]) => [prefix + k, v])));
}

export function addDays(s: string, n: number) {
  const d = new Date(s + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
function addMonths(s: string, n: number) {
  const d = new Date(s + 'T00:00:00Z');
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + n);
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, last));
  return d.toISOString().slice(0, 10);
}
export function currentWindow(s: ReportState): [string, string] {
  if (s.master === 'Custom Date Range') {
    return [s.from, s.to];
  }
  const day = s.selectedDate;
  if (!validDate(day)) {
    return [s.from, s.to];
  }
  const d = new Date(day + 'T00:00:00Z');
  const y = d.getUTCFullYear(),
    m = d.getUTCMonth();
  if (s.cadence === 'Daily') {
    return [day, day];
  }
  if (s.cadence === 'Weekly') {
    const start = addDays(day, -((d.getUTCDay() + 6) % 7));
    return [start, addDays(start, 6)];
  }
  if (s.cadence === 'Monthly - MTD') {
    return [day.slice(0, 7) + '-01', day];
  }
  if (s.cadence === 'Yearly - YTD') {
    return [day.slice(0, 4) + '-01-01', day];
  }
  if (s.cadence === 'Yearly') {
    return [`${y}-01-01`, `${y}-12-31`];
  }
  const start =
    s.cadence === 'Quarterly'
      ? `${y}-${String(Math.floor(m / 3) * 3 + 1).padStart(2, '0')}-01`
      : day.slice(0, 7) + '-01';
  return [start, addDays(addMonths(start, s.cadence === 'Quarterly' ? 3 : 1), -1)];
}
export function priorWindow(from: string, to: string, comparison: ReportState['comparison']): [string, string] {
  if (!validDate(from) || !validDate(to)) {
    return [from, to];
  }
  const length = Math.round((Date.parse(to) - Date.parse(from)) / 86400000);
  if (comparison === 'Previous Period') {
    return [addDays(from, -length - 1), addDays(from, -1)];
  }
  const start = addMonths(from, comparison === 'Previous Year' ? -12 : comparison === 'Previous Quarter' ? -3 : -1);
  return [start, addDays(start, length)];
}
export const selectionComparisons: Record<string, Array<string>> = {
  Daily: ['DoD Day Prior'],
  Weekly: ['WoW Week Prior'],
  Monthly: ['MoM Last Month', 'MoM Same Month LY'],
  'Monthly - MTD': ['MoM MTD Last Month'],
  Quarterly: ['QoQ Last Quarter', 'QoQ Same Quarter LY'],
  Yearly: ['YoY Last Year'],
  'Yearly - YTD': ['YoY YTD Last Year'],
};
export function comparisonWindow(s: ReportState): [string, string] {
  const [from, to] = currentWindow(s);
  if (s.master === 'Custom Date Range') {
    return priorWindow(from, to, s.comparison);
  }
  const choice = s.selectionComparison;
  const date =
    choice === 'DoD Day Prior'
      ? addDays(s.selectedDate, -1)
      : choice === 'WoW Week Prior'
        ? addDays(s.selectedDate, -7)
        : addMonths(s.selectedDate, /LY|Last Year/.test(choice) ? -12 : choice === 'QoQ Last Quarter' ? -3 : -1);
  return currentWindow({ ...s, selectedDate: date });
}

export type MetricSpec = {
  key: string;
  label: string;
  format: 'money' | 'percent' | 'count';
  group: 'sales' | 'margin' | 'fees' | 'marketing' | 'cm' | 'target';
  start?: boolean;
};
function metric(
  key: string,
  label: string,
  group: MetricSpec['group'],
  format: MetricSpec['format'] = 'money',
  start = false,
): MetricSpec {
  return { key, label, group, format, start };
}
export const financialMetrics: Array<MetricSpec> = [
  metric('credit_sales_aud', 'TTV - Credit', 'sales'),
  metric('cash_sales_aud', 'TTV - Cash', 'sales'),
  metric('ttvOthers', 'TTV - Others', 'sales'),
  metric('credit_refund_aud', 'Refunds - Credit', 'sales'),
  metric('cash_refund_aud', 'Refunds - Cash', 'sales'),
  metric('refundOthers', 'Refunds - Others', 'sales'),
  metric('total_refund_aud', 'Refunds - Total', 'sales'),
  metric('refundRate', 'Refunds %', 'sales', 'percent'),
  metric('gross_sales_aud_ar', 'TTV (AR)', 'sales'),
  metric('margin_aud_br', 'GM (BR)', 'margin', 'money', true),
  metric('gm', 'GM (AR)', 'margin'),
  metric('gmRate', 'GM (AR) % (GM AR / TTV AR)', 'margin', 'percent'),
  metric('bank', 'Bank Fees', 'fees', 'money', true),
  metric('commission', 'Commissions (0.77% of TTV AR)', 'fees'),
  metric('customer_discount', 'Customer Discounts (promo excl. cancels)', 'fees'),
  metric('interest_income', 'Interest Income', 'fees'),
  metric('vcc_rebate', 'VCC Rebate', 'fees'),
  metric('netMargin', 'Net Margin (after fees, comms, discounts)', 'margin', 'money', true),
  metric('service_fee', 'Service Fees', 'fees'),
  metric('new_customers', 'New Customers (BR)', 'fees', 'count'),
  metric('new_customer_gm_br', 'GM (BR) New Customer', 'margin'),
  metric('new_customer_gm_ar', 'GM (AR) New Customer', 'margin'),
  ...[
    'Affiliates',
    'Apple Search Ads',
    'ChatGPT Ads',
    'GHA',
    'PMax',
    'Paid Display',
    'Paid Search',
    'Paid Search (Brand)',
    'Paid Social',
    'Tiktok Ads',
    'Velocity Spend',
    'Manual Markeing Spend',
  ].map((c, i) =>
    metric('channel:' + c, c === 'Manual Markeing Spend' ? 'Manual Marketing Spend' : c, 'marketing', 'money', i === 0),
  ),
  metric('cost_aud', 'Total Marketing Spend', 'marketing'),
  metric('ttvRoas', 'Net TTV ROAS', 'margin', 'percent', true),
  metric('roas', 'Net Margin ROAS', 'margin', 'percent'),
  metric('gross', 'Gross CM (gm ar - marketing costs)', 'cm', 'money', true),
  metric('net', 'Net CM (net margin - marketing costs)', 'cm'),
];
export function metricsFor(view: ReportView): Array<MetricSpec> {
  if (view === 'Daily CM Tracking') {
    return financialMetrics.filter((m) => m.key !== 'service_fee');
  }
  const list = financialMetrics.filter((m) => m.key !== 'channel:ChatGPT Ads');
  if (view === 'Regions Monthly CM Tracking') {
    return list;
  }
  return [
    ...list,
    metric('target', 'Net CM Target', 'target', 'money', true),
    metric('targetVariance', 'Net CM Variance (actual margin - margin target)', 'target'),
    metric('targetAchievement', 'Net CM Achievement (% of actual margin / margin target)', 'target', 'percent'),
  ];
}
export function formatMetric(value: Value, format: MetricSpec['format'], decimals = 0) {
  if (value === null || !Number.isFinite(value)) {
    return 'Unknown';
  }
  return (
    (format === 'percent' ? value * 100 : value).toLocaleString('en-AU', {
      maximumFractionDigits: decimals,
      minimumFractionDigits: decimals,
    }) + (format === 'percent' ? '%' : '')
  );
}

export type CMPageContext = {
  report: string;
  view: string;
  filters: ReportState;
  source: string;
  snapshotThrough: string;
  extractedAt: string;
  metrics: Metrics;
  limitations: Array<string>;
  selection?: Record<string, string>;
};
